import React, { useState, useEffect, useRef } from 'react';

const WalletConnection = ({ userAccount, hasActiveAccount, onConnect, onUnlock, onDisconnect, accountData, provider, currentChainId, onSelectWallet, privyIdentityLabel, onSwitchAccount, switchingAccount }) => {

    const [copied, setCopied] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRootRef = useRef(null);
    const copyTimerRef = useRef(null);

    const handleConnectClick = async () => {
        // Use the onConnect prop to open the shared modal from App.js
        if (onConnect) {
            onConnect();
        }
    };

    // Check if wallet is locked (has account but no provider/signer)
    const isWalletLocked = userAccount && !provider;

    const formatAddress = (address) => {
        return `${address.slice(0, 6)}...${address.slice(-4)}`;
    };

    // Close the account menu on an outside click or Escape, the two ways every
    // other menu-shaped control in a browser is expected to close.
    useEffect(() => {
        if (!menuOpen) return undefined;

        const handlePointerDown = (event) => {
            if (menuRootRef.current && !menuRootRef.current.contains(event.target)) {
                setMenuOpen(false);
            }
        };
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') setMenuOpen(false);
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('touchstart', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('touchstart', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [menuOpen]);

    // The menu unmounts as soon as the wallet disconnects, which is exactly when
    // a pending "Copied" timer would fire into a dead component.
    useEffect(() => () => {
        if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    }, []);

    // Nothing in the menu applies once the wallet is gone.
    useEffect(() => {
        if (!userAccount) setMenuOpen(false);
    }, [userAccount]);

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
            copyTimerRef.current = setTimeout(() => setCopied(false), 2000);
        }).catch(err => {
            console.error('Failed to copy: ', err);
        });
    };

    const handleCopyClick = () => {
        if (userAccount) {
            copyToClipboard(userAccount);
        }
    };

    const handleSwitchClick = () => {
        setMenuOpen(false);
        onSwitchAccount?.();
    };

    const handleDisconnectClick = () => {
        setMenuOpen(false);
        onDisconnect?.();
    };

    return (
        <div className="wallet-connection">
            {userAccount ? (
                <div className="wallet-bar">
                    {isWalletLocked ? (
                        <button className="connect-button" onClick={onUnlock || onConnect}>Unlock Wallet</button>
                    ) : (
                        /* One trigger instead of a row of look-alike pills: the address is
                           the identity people recognise, and everything you can do to the
                           account hangs off it, labelled and grouped. */
                        <div className="wallet-account" ref={menuRootRef}>
                            <button
                                type="button"
                                className="wallet-address-button"
                                onClick={() => setMenuOpen((open) => !open)}
                                aria-haspopup="menu"
                                aria-expanded={menuOpen}
                                title="Account options"
                            >
                                <span className="wallet-address-text">
                                    {formatAddress(userAccount)}
                                </span>
                                <i
                                    className={`fas fa-chevron-down wallet-account-caret ${menuOpen ? 'is-open' : ''}`}
                                    aria-hidden="true"
                                />
                            </button>

                            {menuOpen ? (
                                <div className="wallet-account-menu" role="menu">
                                    {privyIdentityLabel ? (
                                        <div className="wallet-account-identity">
                                            <span className="wallet-account-identity-label">Signed in as</span>
                                            <span
                                                className="wallet-account-identity-value"
                                                title={privyIdentityLabel}
                                            >
                                                {privyIdentityLabel}
                                            </span>
                                        </div>
                                    ) : null}

                                    <button
                                        type="button"
                                        className="wallet-account-item"
                                        role="menuitem"
                                        onClick={handleCopyClick}
                                    >
                                        <i
                                            className={`fas ${copied ? 'fa-check' : 'fa-copy'}`}
                                            aria-hidden="true"
                                        />
                                        <span>{copied ? 'Address copied' : 'Copy address'}</span>
                                    </button>

                                    {onSwitchAccount ? (
                                        <button
                                            type="button"
                                            className="wallet-account-item"
                                            role="menuitem"
                                            onClick={handleSwitchClick}
                                            disabled={switchingAccount}
                                        >
                                            <i className="fas fa-right-left" aria-hidden="true" />
                                            <span>
                                                {switchingAccount ? 'Switching…' : 'Change account'}
                                            </span>
                                        </button>
                                    ) : null}

                                    <button
                                        type="button"
                                        className="wallet-account-item wallet-account-item--danger"
                                        role="menuitem"
                                        onClick={handleDisconnectClick}
                                    >
                                        <i className="fas fa-arrow-right-from-bracket" aria-hidden="true" />
                                        <span>Disconnect wallet</span>
                                    </button>
                                </div>
                            ) : null}
                        </div>
                    )}
                </div>
            ) : (
                <button className="connect-button" onClick={handleConnectClick}>Connect</button>
            )}
        </div>
    );
};

export default WalletConnection;
